import { useState, useRef, useCallback } from 'react';
import unifiedAuth from '../utils/unifiedAuth';

const CHUNK_SIZE = 16 * 1024 * 1024; // 16MB chunks for better 4K performance
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

export interface UseChunkedUploadOptions {
  onUploadComplete: (result: unknown) => void;
  onUploadError: (error: string) => void;
  onProgress?: (progress: UploadProgress) => void;
  maxFileSize?: number;
  uploadEndpoint?: string;
}

export interface UploadProgress {
  percentage: number;
  uploadedBytes: number;
  totalBytes: number;
  speed: number; // bytes per second
  timeRemaining: number; // seconds
  currentChunk: number;
  totalChunks: number;
}

export interface UploadState {
  status: 'idle' | 'uploading' | 'paused' | 'completed' | 'error';
  progress: UploadProgress;
  error: string | null;
  isPaused: boolean;
  uploadId: string | null;
}

export const useChunkedUpload = (options: UseChunkedUploadOptions) => {
  const {
    onUploadComplete,
    onUploadError,
    onProgress,
    maxFileSize = 25 * 1024 * 1024 * 1024, // 25GB default for 4K videos
    uploadEndpoint = '/api/upload'
  } = options;

  const [uploadState, setUploadState] = useState<UploadState>({
    status: 'idle',
    progress: {
      percentage: 0,
      uploadedBytes: 0,
      totalBytes: 0,
      speed: 0,
      timeRemaining: 0,
      currentChunk: 0,
      totalChunks: 0
    },
    error: null,
    isPaused: false,
    uploadId: null,
  });

  const uploadStartTime = useRef<number>(0);
  const abortController = useRef<AbortController | null>(null);
  const isPausedRef = useRef<boolean>(false);
  const currentStatusRef = useRef<string>('idle');

  const validateFile = useCallback((file: File): string | null => {
    if (file.size > maxFileSize) {
      return `File size exceeds maximum allowed size of ${formatBytes(maxFileSize)}`;
    }
    return null;
  }, [maxFileSize]);

  const initializeUpload = async (file: File): Promise<string> => {
    const response = await unifiedAuth.fetch(`${uploadEndpoint}/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filename: file.name,
        filesize: file.size,
        contentType: file.type,
        totalChunks: Math.ceil(file.size / CHUNK_SIZE)
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      if (response.status === 403) {
        throw new Error('Authentication failed. Please log in again to upload files.');
      }
      
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || `HTTP ${response.status}` };
      }
      throw new Error(errorData.message || 'Failed to initialize upload');
    }

    const data = await response.json();
    return data.data.uploadId;
  };

  const uploadChunk = async (
    file: File,
    uploadId: string,
    chunkIndex: number,
    retryCount = 0
  ): Promise<void> => {
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('uploadId', uploadId);
    formData.append('chunkIndex', chunkIndex.toString());
    formData.append('totalChunks', Math.ceil(file.size / CHUNK_SIZE).toString());
    formData.append('chunk', chunk);

    try {
      // The caller's AbortSignal is passed through untouched, so cancelling an
      // upload still works and no 30s default deadline is imposed on a chunk.
      const response = await unifiedAuth.fetch(`${uploadEndpoint}/chunk`, {
        method: 'POST',
        body: formData,
        signal: abortController.current?.signal
      });

      if (!response.ok) {
        let errorMessage = `Chunk ${chunkIndex + 1} upload failed (HTTP ${response.status})`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If we can't parse JSON, use the status text
          errorMessage = `${errorMessage}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw error; // Don't retry if aborted
      }

      if (retryCount < MAX_RETRIES) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
        return uploadChunk(file, uploadId, chunkIndex, retryCount + 1);
      } else {
        throw error;
      }
    }
  };

  const finalizeUpload = async (uploadId: string): Promise<unknown> => {
    const response = await unifiedAuth.fetch(`${uploadEndpoint}/finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uploadId })
    });

    if (!response.ok) {
      let errorMessage = `Upload finalization failed (HTTP ${response.status})`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.data;
  };

  const updateProgress = useCallback((
    currentChunk: number,
    totalChunks: number,
    uploadedBytes: number,
    totalBytes: number
  ) => {
    const now = Date.now();
    const elapsed = (now - uploadStartTime.current) / 1000; // seconds
    const speed = elapsed > 0 ? uploadedBytes / elapsed : 0;
    const timeRemaining = speed > 0 ? (totalBytes - uploadedBytes) / speed : 0;

    const progress: UploadProgress = {
      percentage: (uploadedBytes / totalBytes) * 100,
      uploadedBytes,
      totalBytes,
      speed,
      timeRemaining,
      currentChunk,
      totalChunks
    };

    setUploadState(prev => ({ ...prev, progress }));
    onProgress?.(progress);
  }, [onProgress]);

  const startUpload = async (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      currentStatusRef.current = 'error';
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: validationError
      }));
      onUploadError(validationError);
      return;
    }

    try {
      uploadStartTime.current = Date.now();
      abortController.current = new AbortController();

      setUploadState(prev => ({
        ...prev,
        status: 'uploading',
        error: null,
        isPaused: false
      }));
      
      // Update refs to avoid stale closures
      isPausedRef.current = false;
      currentStatusRef.current = 'uploading';

      // Initialize upload with timeout
      const initTimeout = setTimeout(() => {
        throw new Error('Upload initialization timed out. Please check your connection and try again.');
      }, 30000);
      
      const uploadId = await initializeUpload(file);
      clearTimeout(initTimeout);
      
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      
      setUploadState(prev => ({
        ...prev,
        uploadId,
        progress: {
          ...prev.progress,
          totalChunks,
          totalBytes: file.size
        }
      }));

      // Upload chunks sequentially
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        
        // Check if paused - use ref to avoid stale closure
        while (isPausedRef.current && currentStatusRef.current === 'uploading') {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Check if cancelled
        if (currentStatusRef.current !== 'uploading') {
          return;
        }

        await uploadChunk(file, uploadId, chunkIndex);

        // Update progress
        const uploadedBytes = Math.min((chunkIndex + 1) * CHUNK_SIZE, file.size);
        updateProgress(chunkIndex + 1, totalChunks, uploadedBytes, file.size);
      }

      // Finalize upload
      const result = await finalizeUpload(uploadId);

      currentStatusRef.current = 'completed';
      setUploadState(prev => ({
        ...prev,
        status: 'completed'
      }));

      onUploadComplete(result);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please check your connection and try again.';
      
      currentStatusRef.current = 'error';
      setUploadState(prev => ({
        ...prev,
        status: 'error',
        error: errorMessage
      }));

      onUploadError(errorMessage);
    }
  };

  const pauseUpload = () => {
    isPausedRef.current = true;
    setUploadState(prev => ({
      ...prev,
      isPaused: true
    }));
  };

  const resumeUpload = () => {
    isPausedRef.current = false;
    setUploadState(prev => ({
      ...prev,
      isPaused: false
    }));
  };

  const cancelUpload = () => {
    abortController.current?.abort();
    
    // Update refs
    isPausedRef.current = false;
    currentStatusRef.current = 'idle';
    
    setUploadState({
      status: 'idle',
      progress: {
        percentage: 0,
        uploadedBytes: 0,
        totalBytes: 0,
        speed: 0,
        timeRemaining: 0,
        currentChunk: 0,
        totalChunks: 0
      },
      error: null,
      isPaused: false,
      uploadId: null,
    });
  };

  return {
    uploadState,
    startUpload,
    pauseUpload,
    resumeUpload,
    cancelUpload,
  };
};

// Helper functions
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
